import { SectionHeader } from "../../SectionHeader";
import { ModuleGrid } from "../../../layouts/ModuleGrid";
import { useIsPortraitMobile } from "../../../hooks/useIsPortraiteMobile";
import TiltMessage from "../../../components/feedback/TiltMessage";
import { useMemo, useState } from "react";
import { format, startOfMonth, endOfMonth } from "date-fns";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import { Card } from "../../ui/card";
import { SummaryHeader } from "../../ui/SummaryHeader";
import { MonthYearPicker } from "../../ui/month-Year-Picker";
import { WholesaleManagerialMetrics } from "../components/wholesale-metrics";
import { WholesaleMultiChannel } from "../components/wholesale-multichannel";
import { WholesaleRevenueOverview } from "../components/wholesale-accounting-rev";
import { WholesaleExpenseReport } from "../components/wholesale-ExpenseReport";
import { WholesaleCategoryPerformance } from "../components/wholesale-categoryPerformance";
import { WholesaleAOVFrequency } from "../components/wholesale-AOV";
import { WholesaleRevenueChart } from "../components/wholesale-revenueBreakdown";
import { WholesaleGenderCategory } from "../components/wholesale-genderCategory";
import { WholesaleColorPerformance } from "../components/wholesale-colorPerformance";
import { WholesaleCollectionPerformance } from "../components/wholesale-collectionPerformance";
import { WholesaleClassPerformance } from "../components/wholesale-classPerformance";
import { WholesaleStyleColorPerformance } from "../components/wholesale-styleColorPerformance";
import { WholesaleABCRankingTable } from "../components/wholesale-accountStoreTable";
import { WholesaleCompletePerformance } from "../components/wholesale-performanceTable";
import { useAccountingWholesale } from "../hooks/useAccountingWholesale";
import { useWholesaleDataCheck } from "../hooks/useWholesaleDataCheck";
import { FileWarning, TriangleAlert } from "lucide-react";
import { useWholesaleSales } from "../hooks/useSalesWholesale";


export function WholesalePage() {
    const now = new Date();
    const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date(now.getFullYear(), now.getMonth()));
    const formattedDate = selectedDate ? format(selectedDate, "yyyy-MM") : "";
    const [categorySelectedDate, setCategorySelectedDate] = useState<Date | undefined>(new Date(now.getFullYear(), now.getMonth()));
    const categoryFormattedDate = categorySelectedDate ? format(categorySelectedDate, "yyyy-MM") : "";

    // dates for the category picker (separate fetch — do not combine)
    const categoryStartDate = format(startOfMonth(new Date(categorySelectedDate || now)), "yyyy-MM-dd");
    const categoryEndDate = format(endOfMonth(new Date(categorySelectedDate || now)), "yyyy-MM-dd");

    const { data: accountingData } = useAccountingWholesale("month");
    // fetch sales data using the category picker range so messages follow that picker
    const { data: salesData } = useWholesaleSales(categoryStartDate, categoryEndDate);

    const revenueData = accountingData?.revenue_overview;
    const wholesaleCategoryData = salesData?.wholesale_category_performance || [];
    const showTiltMessage = useIsPortraitMobile();

    const { hasDataForSelectedMonth: hasRevenueData } = useWholesaleDataCheck(revenueData, {
        selectedMonth: formattedDate,
    });
    const { hasAnyData: hasCategoryDataForSelectedMonth } = useWholesaleDataCheck(wholesaleCategoryData, {
        selectedMonth: categoryFormattedDate,
    });

    // Combine the checks for both datasets
    const hasDataForSelectedMonth = hasRevenueData;

    const categoryHasDataForSelectedMonth = hasCategoryDataForSelectedMonth;

    const isCurrentMonthIncomplete = useMemo(() => {
        if (!selectedDate) return false;
        const selectedMonthStart = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
        const selectedMonthEnd = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);
        return now >= selectedMonthStart && now <= selectedMonthEnd && now.getDate() < selectedMonthEnd.getDate();
    }, [selectedDate, now]);

    console.log("hasRevenueData:", hasRevenueData);
    
    console.log("hasDataForSelectedMonth:", hasDataForSelectedMonth);

    return (
        <div>
            <TiltMessage show={showTiltMessage} />
            <div className={showTiltMessage ? "blur-sm pointer-events-none" : ""}>
                <SectionHeader title="Wholesale" />
                <div className="flex flex-row items-center justify-between">
                    <div className="mb-4">
                        <MonthYearPicker selected={selectedDate} onChange={setSelectedDate} />
                    </div>
                    <div>
                        {!hasDataForSelectedMonth && (
                            <div className="text-sm text-red-600 bg-white/70 dark:bg-black/70 rounded-xl shadow-xl-soft px-3 py-2 mb-4 flex flex-row items-center">
                                <FileWarning /> Missing file uploads for the selected month.
                            </div>
                        )}
                        {isCurrentMonthIncomplete && (
                            <div className="text-sm text-yellow-200 bg-white/70 dark:bg-black/70 rounded-xl shadow-xl-soft px-3 py-2 flex flex-row items-center">
                                <TriangleAlert /> The current month is incomplete.
                            </div>
                        )}
                    </div>
                </div>

                <ModuleGrid>
                    <>
                        <div className="col-span-6 flex flex-col h-full">
                            <WholesaleManagerialMetrics selectedMonth={formattedDate} />
                        </div>
                        <div className="col-span-6 flex flex-col h-full">
                            <WholesaleRevenueChart />
                        </div>
                        <div className="col-span-12 sm:col-span-6 2xl:col-span-3 flex flex-col h-full">
                            <WholesaleRevenueOverview />
                        </div>
                        <div className="col-span-12 sm:col-span-6 2xl:col-span-3 flex flex-col h-full">
                            <WholesaleExpenseReport />
                        </div>
                        <div className="col-span-6 flex flex-col h-full">
                            <WholesaleABCRankingTable />
                        </div>
                        <div className="col-span-6 flex flex-col h-full">
                            <WholesaleMultiChannel />
                        </div>
                        <div className="col-span-6 flex flex-col h-full">
                            <SummaryHeader title="Category Performance" />
                            <Card>
                                <Tabs defaultValue="general">
                                    <div className="flex justify-between items-center w-full">
                                        <TabsList className="m-4">
                                            <TabsTrigger value="general">General</TabsTrigger>
                                            <TabsTrigger value="gender">Gender</TabsTrigger>
                                            <TabsTrigger value="collection">Collection</TabsTrigger>
                                            <TabsTrigger value="class">Class</TabsTrigger>
                                            <TabsTrigger value="color">Color</TabsTrigger>
                                            <TabsTrigger value="s/c">S/C</TabsTrigger>
                                        </TabsList>
                                        <div className="flex flex-row">
                                        {!categoryHasDataForSelectedMonth && (
                                            <div className="text-sm text-red-600 bg-white/70 dark:bg-black/70 rounded-lg shadow-xl-soft px-1 py-2 mb-2 flex flex-row items-center">
                                                <FileWarning /> Missing file uploads for the selected month.
                                            </div>
                                        )}
                                        <div className="pr-3 ml-2">
                                            <MonthYearPicker selected={categorySelectedDate} onChange={setCategorySelectedDate} />
                                        </div>
                                        </div>
                                    </div>

                                    <TabsContent value="general">
                                        <div className="col-span-6 flex flex-col h-full">
                                            <WholesaleCategoryPerformance selectedMonth={categoryFormattedDate} />
                                        </div>
                                    </TabsContent>
                                    <TabsContent value="gender">
                                        <div className="col-span-12 sm:col-span-6 2xl:col-span-3 flex flex-col h-full">
                                            <WholesaleGenderCategory selectedMonth={categoryFormattedDate} />
                                        </div>
                                    </TabsContent>
                                    <TabsContent value="collection">
                                        <div className="col-span-12 sm:col-span-6 2xl:col-span-3 flex flex-col h-full">
                                            <WholesaleCollectionPerformance selectedMonth={categoryFormattedDate} />
                                        </div>
                                    </TabsContent>
                                    <TabsContent value="class">
                                        <div className="col-span-12 sm:col-span-6 2xl:col-span-3 flex flex-col h-full">
                                            <WholesaleClassPerformance selectedMonth={categoryFormattedDate} />
                                        </div>
                                    </TabsContent>
                                    <TabsContent value="color">
                                        <div className="col-span-12 sm:col-span-6 2xl:col-span-3 flex flex-col h-full">
                                            <WholesaleColorPerformance selectedMonth={categoryFormattedDate} />
                                        </div>
                                    </TabsContent>
                                    <TabsContent value="s/c">
                                        <div className="col-span-6 flex flex-col h-full">
                                            <WholesaleStyleColorPerformance selectedMonth={categoryFormattedDate} />
                                        </div>
                                    </TabsContent>
                                </Tabs>
                            </Card>
                        </div>
                        <div className="col-span-6 flex flex-col h-full">
                            <WholesaleCompletePerformance selectedMonth={formattedDate} />
                        </div>
                        <div className="col-span-6 flex flex-col h-full">
                            <WholesaleAOVFrequency />
                        </div>
                    </>
                </ModuleGrid>
            </div>
        </div>
    );
}